import { getFontStack } from '../../config';
import { getBlockStyle, resolveRunStyle, textToRuns } from './textRuns';

let measureContext = null;
const metricsCache = new Map();

const getMeasureContext = () => {
  if (!measureContext && typeof document !== 'undefined') {
    measureContext = document.createElement('canvas').getContext('2d');
  }
  return measureContext;
};

const buildCanvasFont = (style) =>
  `${style.italic ? 'italic' : 'normal'} ${style.bold ? 'bold' : 'normal'} ${style.fontSize}px ${getFontStack(
    style.fontFamily,
  )}`;

const measureWidth = (ctx, text, letterSpacing) => {
  if (!text) {
    return 0;
  }
  if (!ctx) {
    return text.length * 10;
  }
  if ('letterSpacing' in ctx) {
    ctx.letterSpacing = `${letterSpacing}px`;
    return ctx.measureText(text).width;
  }
  return ctx.measureText(text).width + letterSpacing * text.length;
};

const getVerticalMetrics = (ctx, font, fontSize) => {
  const key = font;
  if (!metricsCache.has(key)) {
    let ascent = fontSize * 0.8;
    let descent = fontSize * 0.2;
    if (ctx) {
      ctx.font = font;
      const metrics = ctx.measureText('Hg');
      if (metrics.fontBoundingBoxAscent) {
        ascent = metrics.fontBoundingBoxAscent;
        descent = metrics.fontBoundingBoxDescent;
      }
    }
    metricsCache.set(key, { ascent, descent });
  }
  return metricsCache.get(key);
};

export const clearTextMetricsCache = () => metricsCache.clear();

const tokenize = (runs, blockStyle) => {
  const tokens = [];
  runs.forEach((run) => {
    const style = resolveRunStyle(blockStyle, run.style);
    const re = /(\n|[^\S\n]+|[^\s]+)/g;
    let match = re.exec(run.text);
    while (match) {
      const text = match[0];
      tokens.push({ text, style, isBreak: text === '\n', isSpace: /^[^\S\n]+$/.test(text) });
      match = re.exec(run.text);
    }
  });
  return tokens;
};

export const layoutText = (element, widthOverride) => {
  const ctx = getMeasureContext();
  const blockStyle = getBlockStyle(element);
  const lineHeight = element.lineHeight || 1.2;
  const letterSpacing = element.letterSpacing || 0;
  const align = element.align || 'left';
  const background = element.background || {};
  const padding = background.enabled ? background.padding || 0 : 0;
  const boxWidth = Math.max(10, (widthOverride ?? element.width ?? 10) - padding * 2);
  const runs = element.runs || textToRuns(element.text);

  const lines = [];
  let current = { tokens: [], width: 0, lastStyle: blockStyle };

  const pushLine = (style) => {
    lines.push(current);
    current = { tokens: [], width: 0, lastStyle: style || current.lastStyle };
  };

  const addToken = (token, width) => {
    current.tokens.push({ ...token, width });
    current.width += width;
    current.lastStyle = token.style;
  };

  tokenize(runs, blockStyle).forEach((token) => {
    if (token.isBreak) {
      current.lastStyle = token.style;
      pushLine(token.style);
      return;
    }

    if (ctx) {
      ctx.font = buildCanvasFont(token.style);
    }
    const width = measureWidth(ctx, token.text, letterSpacing);

    if (token.isSpace) {
      addToken(token, width);
      return;
    }

    if (current.tokens.length && current.width + width > boxWidth) {
      while (current.tokens.length && current.tokens[current.tokens.length - 1].isSpace) {
        const removed = current.tokens.pop();
        current.width -= removed.width;
      }
      pushLine();
    }

    if (width <= boxWidth || token.text.length === 1) {
      addToken(token, width);
      return;
    }

    let chunk = '';
    Array.from(token.text).forEach((char) => {
      const next = chunk + char;
      if (chunk && current.width + measureWidth(ctx, next, letterSpacing) > boxWidth) {
        addToken({ ...token, text: chunk }, measureWidth(ctx, chunk, letterSpacing));
        pushLine();
        chunk = char;
      } else {
        chunk = next;
      }
    });
    if (chunk) {
      addToken({ ...token, text: chunk }, measureWidth(ctx, chunk, letterSpacing));
    }
  });
  pushLine();

  let y = padding;
  const laidOut = lines.map((line) => {
    const styles = line.tokens.length ? line.tokens.map((token) => token.style) : [line.lastStyle];
    let ascent = 0;
    let descent = 0;
    let maxSize = 0;
    styles.forEach((style) => {
      const metrics = getVerticalMetrics(ctx, buildCanvasFont(style), style.fontSize);
      ascent = Math.max(ascent, metrics.ascent);
      descent = Math.max(descent, metrics.descent);
      maxSize = Math.max(maxSize, style.fontSize);
    });
    const height = maxSize * lineHeight;
    const baseline = y + (height - (ascent + descent)) / 2 + ascent;

    let visibleWidth = line.width;
    for (let i = line.tokens.length - 1; i >= 0 && line.tokens[i].isSpace; i -= 1) {
      visibleWidth -= line.tokens[i].width;
    }
    let x = padding;
    if (align === 'center') {
      x += (boxWidth - visibleWidth) / 2;
    } else if (align === 'right') {
      x += boxWidth - visibleWidth;
    }

    const fragments = line.tokens.map((token) => {
      const fragment = { text: token.text, x, width: token.width, style: token.style };
      x += token.width;
      return fragment;
    });

    const result = { top: y, height, baseline, fragments };
    y += height;
    return result;
  });

  return {
    lines: laidOut,
    letterSpacing,
    height: Math.ceil(y + padding),
  };
};

export const measureNaturalWidth = (element) => {
  const layout = layoutText(element, Number.MAX_SAFE_INTEGER / 4);
  const padding = element.background?.enabled ? element.background.padding || 0 : 0;
  const widest = layout.lines.reduce((max, line) => {
    const visible = line.fragments.filter((fragment) => fragment.text.trim());
    if (!visible.length) {
      return max;
    }
    const last = visible[visible.length - 1];
    return Math.max(max, last.x + last.width - visible[0].x);
  }, 0);
  return Math.ceil(widest + padding * 2 + 2);
};

export const drawTextLayout = (ctx, layout, outline) => {
  ctx.textBaseline = 'alphabetic';
  if ('letterSpacing' in ctx) {
    ctx.letterSpacing = `${layout.letterSpacing}px`;
  }

  const eachFragment = (draw) => {
    layout.lines.forEach((line) => {
      line.fragments.forEach((fragment) => {
        ctx.font = buildCanvasFont(fragment.style);
        draw(fragment, line);
      });
    });
  };

  const drawDecorations = (fragment, line, paint) => {
    const { fontSize, underline, strike } = fragment.style;
    const thickness = Math.max(1, fontSize * 0.06);
    if (underline) {
      paint(fragment.x, line.baseline + fontSize * 0.1, fragment.width, thickness);
    }
    if (strike) {
      paint(fragment.x, line.baseline - fontSize * 0.3, fragment.width, thickness);
    }
  };

  if (outline?.enabled && outline.width > 0) {
    ctx.lineJoin = 'round';
    ctx.lineWidth = outline.width * 2;
    ctx.strokeStyle = outline.color;
    eachFragment((fragment, line) => {
      ctx.strokeText(fragment.text, fragment.x, line.baseline);
      drawDecorations(fragment, line, (x, y, w, h) => ctx.strokeRect(x, y, w, h));
    });
    ctx.shadowColor = 'transparent';
  }

  eachFragment((fragment, line) => {
    ctx.fillStyle = fragment.style.fill;
    ctx.fillText(fragment.text, fragment.x, line.baseline);
    drawDecorations(fragment, line, (x, y, w, h) => ctx.fillRect(x, y, w, h));
  });
};
